import ContactLink from "@/components/ContactLink";
import { Head } from "@inertiajs/react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SectionHeading from "@/components/marketing/SectionHeading";

export default function Terms() {
  return (
    <MarketingLayout>
      <Head title="Terms of Service" />

      <section className="py-10 md:py-14">
        <div className="container">
          <div className="prose max-w-3xl mx-auto text-foreground">
            <SectionHeading
              as="h1"
              flourish="legal fine print"
              title="Terms of Service"
              align="left"
              className="mb-4"
            />
            <p className="text-sm text-muted-foreground mb-10">
              Last updated: September 30, 2026
            </p>

            <p className="mb-4 leading-relaxed">
              These Terms of Service ("Terms") govern your use of PagePledge
              ("the Service"), operated by PagePledge Inc. ("we," "our," or
              "us"). By creating an account, submitting an application, making
              a donation, or otherwise using the Service, you agree to these
              Terms.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              1. Who can use PagePledge
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Schools:</strong> Canadian K-12 schools (public,
                independent, charter, First Nations, or home school co-ops)
                may apply to use PagePledge to run fundraising campaigns. All
                applications are reviewed manually by us and approved at our
                discretion.
              </li>
              <li>
                <strong>School staff:</strong> principals, teachers,
                fundraising committee members, and other staff authorized by
                an approved school.
              </li>
              <li>
                <strong>Parents and guardians:</strong> adults 18 or older
                with a child at a participating school.
              </li>
              <li>
                <strong>Donors:</strong> anyone 18 or older making a voluntary
                donation.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              2. Accounts
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                All accounts use passwordless sign-in via magic-link emails.
              </li>
              <li>
                You are responsible for maintaining the confidentiality of
                your email account. Anyone with access to your inbox can sign
                in to PagePledge.
              </li>
              <li>
                You may not create an account using someone else's email.
              </li>
              <li>You may not share sign-in links with others.</li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              3. Permitted use
            </h2>
            <p className="mb-4 leading-relaxed">
              You agree to use the Service only for its intended purpose:
              running school read-a-thon fundraising campaigns in good faith.
              You may not:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Upload false, misleading, or impersonated student information
              </li>
              <li>Attempt to defraud donors</li>
              <li>
                Attempt to access data belonging to schools or students you
                are not authorized to see
              </li>
              <li>
                Attempt to reverse-engineer, scrape, or overload the Service
              </li>
              <li>Use the Service in violation of Canadian law</li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              4. Donations
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>PagePledge never holds donor funds.</strong> Donations
                are processed by Square and flow directly into the recipient
                school's Square account.
              </li>
              <li>
                All donations are voluntary and non-refundable except through
                the recipient school. If you need a refund, contact the school
                directly; they can issue it through their Square dashboard.
              </li>
              <li>
                <strong>Tax receipts:</strong> PagePledge does not issue tax
                receipts. Schools are responsible for issuing tax receipts if
                they are a registered charity and choose to do so.
              </li>
              <li>
                <strong>Processing fees:</strong> donors may choose to cover
                Square's processing fees and/or leave an optional tip to
                PagePledge. Both are clearly disclosed before checkout.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              5. Fees and pricing
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                An <strong>early adopter discount</strong> is available on the flat
                campaign fee. Your price and any discount are confirmed
                before your school starts its campaign.
              </li>
              <li>
                New schools start on a <strong>free trial</strong>, which lets
                them set up a campaign and see how PagePledge works. The trial
                doesn't include running a campaign: going live, accepting
                donations, adding teachers, or emailing families.
              </li>
              <li>
                Running a campaign costs a <strong>flat per-campaign fee</strong>,
                based on the number of students in the campaign and paid before
                the campaign goes live. PagePledge approves each campaign for
                launch once its fee is paid. There are no percentage-based fees
                taken from donations.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              6. Content and data
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Schools retain ownership of all roster data they upload.
              </li>
              <li>
                Parents retain ownership of any content they submit (reading
                minute logs, notes).
              </li>
              <li>Donors retain ownership of donation messages.</li>
              <li>
                By submitting content, you grant PagePledge a limited license
                to display, store, and process it for the purpose of operating
                the Service.
              </li>
              <li>
                You agree not to submit content that is abusive, harassing,
                illegal, or violates anyone else's rights.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              7. Privacy
            </h2>
            <p className="mb-4 leading-relaxed">
              Your use of the Service is also governed by our{" "}
              <a href="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </a>
              .
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              8. Service availability
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                We aim for high availability but do not guarantee
                uninterrupted service.
              </li>
              <li>
                We may perform scheduled maintenance, preferably outside
                active campaign hours.
              </li>
              <li>
                We are not liable for missed donations or lost minute logs due
                to downtime beyond our reasonable control.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              9. Termination
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Schools may end their use of PagePledge at any time by
                contacting us. We will archive their account and retain data
                per the{" "}
                <a href="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </a>
                .
              </li>
              <li>
                We may suspend or terminate an account that violates these
                Terms or is suspected of fraud, with or without notice.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              10. Disclaimers and limitation of liability
            </h2>
            <p className="mb-4 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND,
              EXPRESS OR IMPLIED, TO THE MAXIMUM EXTENT PERMITTED BY LAW. WE
              ARE NOT LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Indirect, incidental, consequential, or punitive damages
              </li>
              <li>
                Lost donations, lost minutes, lost data, or lost revenue
              </li>
              <li>
                Damages exceeding the total amount paid to PagePledge in the
                twelve months preceding the claim (or CAD $100 if no fees
                have been paid)
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Nothing in these Terms limits liability that cannot be limited
              under applicable Canadian law.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              11. Governing law
            </h2>
            <p className="mb-4 leading-relaxed">
              These Terms are governed by the laws of the Province of
              Alberta and the federal laws of Canada. Any disputes will be
              resolved in the courts of Alberta.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              12. Changes
            </h2>
            <p className="mb-4 leading-relaxed">
              We may update these Terms from time to time. Material changes
              will be announced via email to account holders at least 30 days
              before taking effect. Continued use after changes means you
              accept the updated Terms.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              13. Contact
            </h2>
            <p className="mb-4 leading-relaxed">
              Questions about these Terms can be sent to{" "}
              <strong>
                <ContactLink
                  className="text-primary hover:underline"
                >
                  hello@pagepledge.com
                </ContactLink>
              </strong>
              .
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
